import React, { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import type { GestationMethod } from '../types/pregnancy';

interface Props {
  onSave: (data: {
    lmp: string;
    gestationEstablishedBy: GestationMethod;
    gravidity: number;
    parity: number;
    rhesusStatus: string | null;
  }) => Promise<void>;
  onCancel: () => void;
}

export function NewPregnancyModal({ onSave, onCancel }: Props) {
  const [lmp, setLmp] = useState('');
  const [method, setMethod] = useState<GestationMethod>('dates');
  const [gravidity, setGravidity] = useState(1);
  const [parity, setParity] = useState(0);
  const [rhesus, setRhesus] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!lmp) { setError('LMP is required'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({
        lmp,
        gestationEstablishedBy: method,
        gravidity,
        parity,
        rhesusStatus: rhesus || null,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create pregnancy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">New Pregnancy</h2>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        {error && <div className="mb-3 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Menstrual Period (LMP) *</label>
            <input type="date" value={lmp} onChange={e => setLmp(e.target.value)} className="w-full px-3 py-2 border rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gestation Established By</label>
            <select value={method} onChange={e => setMethod(e.target.value as GestationMethod)} className="w-full px-3 py-2 border rounded-md bg-white">
              <option value="dates">Dates (LMP)</option>
              <option value="sonar">Sonar</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gravidity</label>
              <input type="number" min={1} value={gravidity} onChange={e => setGravidity(parseInt(e.target.value) || 1)} className="w-full px-3 py-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parity</label>
              <input type="number" min={0} value={parity} onChange={e => setParity(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border rounded-md" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rhesus Status</label>
            <select value={rhesus} onChange={e => setRhesus(e.target.value)} className="w-full px-3 py-2 border rounded-md bg-white">
              <option value="">Not yet tested</option>
              <option value="positive">Positive</option>
              <option value="negative">Negative</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={!lmp || saving} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating...</> : 'Create Pregnancy'}
          </button>
        </div>
      </div>
    </div>
  );
}
